import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface BankAccount {
  id: number;
  accountName: string;
  holderName: string;
  accountNo: string | null;
  openingBalance: string;
  balanceAmount: string;
  accountType: string;
  status: string;
}

export default function BankAccountsList() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25000);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAccounts();
  }, [page, limit, search]);

  const getAccounts = async () => {
    setLoading(true);
    const response = await request.get("api/getAccounts");
    const allAccounts = [...response.data.bankAccounts];
    if (response.data.cashAccount) {
      allAccounts.unshift(response.data.cashAccount);
    }
    setAccounts(allAccounts);
    setTotalCount(0);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "accountName",
      label: "Account Name",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "holderName",
      label: "Holder Name",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "accountNo",
      label: "Account No",
      minWidth: 150,
      format: (value) => value || "-",
    },
    {
      id: "openingBalance",
      label: "Opening Balance",
      minWidth: 130,
      format: (value) => value,
    },
    {
      id: "balanceAmount",
      label: "Balance Amount",
      minWidth: 130,
      format: (value) => value,
    },
    {
      id: "accountType",
      label: "Account Type",
      minWidth: 100,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === "Active"} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={
            row.accountType !== "Cash"
              ? [
                  {
                    icon: <EditIcon />,
                    tooltip: "Edit",
                    onClick: () => navigate(`/admin/bank-accounts/${row.id}`),
                  },
                ]
              : []
          }
        />
      ),
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Bank Accounts"
        showSearch
        searchPlaceholder="Search accounts..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Account",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/bank-accounts/create");
          },
        }}
      />

      <DataTable
        columns={columns}
        rows={accounts}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
      <DeleteConfirmation
        open={deleteDialog.open}
        title={"Delete Account - " + deleteDialog?.title}
        message="Are you sure you want to delete this account? This action cannot be undone."
        apiEndpoint={"api/deleteAccount/"}
        deleteId={deleteDialog.id}
        onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
        refetchApi={() => {
          setPage(0);
          getAccounts();
        }}
      />
    </PageWrapper>
  );
}
